import React, { useEffect, useState } from 'react';
import LinkGlyph from '@material-ui/icons/Link';
import { Link, useLocation } from 'react-router-dom';
import {
  FacebookIcon,
  FacebookShareButton,
  TwitterIcon,
  TwitterShareButton,
} from 'react-share';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import {
  fetchYowanPublicBlogDetail,
  fetchYowanPublicBlogList,
  YOWAN_CONTENT_PUBLIC_PAGE_SIZE,
} from '../app/yowanContentClient';
const { getYowanRouteMeta } = require('../routes/manifest');

const RELATED_BLOG_PREVIEW_MAX_CHARS = 72;
const RELATED_BLOG_PAGE_SIZE = YOWAN_CONTENT_PUBLIC_PAGE_SIZE;
const SOCIAL_ICON_SIZE = 28;
const YOWAN_PREVIEW_SEARCH = '?domain=yowan';

const isLocalPreviewHost = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
};

const getBlogDetailPreviewSearch = (search = '') => {
  if (search.includes('domain=yowan')) {
    return search;
  }

  return isLocalPreviewHost() ? YOWAN_PREVIEW_SEARCH : search;
};

const appendBlogDetailPreviewQuery = (target, currentSearch = '') => {
  if (!currentSearch.includes('domain=yowan')) {
    return target;
  }

  const searchIndex = target.indexOf('?');
  const pathname = searchIndex >= 0 ? target.slice(0, searchIndex) : target;
  const targetSearch = searchIndex >= 0 ? target.slice(searchIndex + 1) : '';
  const searchParams = new URLSearchParams(targetSearch);

  searchParams.set('domain', 'yowan');

  return `${pathname}?${searchParams.toString()}`;
};

const truncateRelatedExcerpt = (text) => {
  if (!text || text.length <= RELATED_BLOG_PREVIEW_MAX_CHARS) {
    return { excerpt: text, truncated: false };
  }

  return {
    excerpt: text.slice(0, RELATED_BLOG_PREVIEW_MAX_CHARS).trimEnd(),
    truncated: true,
  };
};

const buildBodyPreview = (body = []) => {
  if (Array.isArray(body)) {
    return body.join('\n\n').trim();
  }

  return String(body || '').trim();
};

const getRelatedPreviewText = (entry = {}) => entry.excerpt || entry.bodyPreview || '';

const getBlogAuthorName = (author = null) => {
  if (!author) {
    return '';
  }

  if (typeof author === 'string') {
    return author;
  }

  return author.displayName || author.display_name || author.name || '';
};

const getBlogAuthorUrl = (author = null) => {
  if (!author || typeof author !== 'object') {
    return '';
  }

  return author.profileUrl || author.profile_url || '';
};

const BlogAuthorAttribution = ({ author, className }) => {
  const name = getBlogAuthorName(author);
  const url = getBlogAuthorUrl(author);

  if (!name) {
    return null;
  }

  return (
    <p className={className}>
      <span className="yowan-blog-meta__label">author: </span>
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {name}
        </a>
      ) : (
        name
      )}
    </p>
  );
};

const BlogEntryMeta = ({ author, date, className = '' }) => (
  <div className={className || 'yowan-blog-entry-meta'}>
    <BlogAuthorAttribution author={author} className="yowan-blog-entry-meta__author" />
    {date ? (
      <p className="yowan-blog-entry-meta__date">
        <span className="yowan-blog-meta__label">date: </span>
        <span>{date}</span>
      </p>
    ) : null}
  </div>
);

const YOWAN_CANONICAL_ORIGIN = 'https://yowan.jp';
const YOWAN_DEFAULT_OG_IMAGE = `${YOWAN_CANONICAL_ORIGIN}/yowan/ogp.png`;

const normalizeMetaText = (value = '') => (
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
);

const truncateMetaText = (value = '', maxLength = 160) => {
  const normalized = normalizeMetaText(value);

  return normalized.length > maxLength ? normalized.slice(0, maxLength).trimEnd() : normalized;
};

const getAbsoluteYowanUrl = (value = '') => {
  const rawValue = String(value || '').trim();

  if (!rawValue) {
    return '';
  }

  if (/^https?:\/\//i.test(rawValue)) {
    return rawValue;
  }

  return `${YOWAN_CANONICAL_ORIGIN}${rawValue.startsWith('/') ? rawValue : `/${rawValue}`}`;
};

const getOrCreateMetaTag = (selector, createAttributes = {}) => {
  if (typeof document === 'undefined') {
    return null;
  }

  let tag = document.querySelector(selector);

  if (!tag) {
    tag = document.createElement('meta');
    Object.entries(createAttributes).forEach(([key, value]) => {
      tag.setAttribute(key, value);
    });
    document.head.appendChild(tag);
  }

  return tag;
};

const setMetaContent = (selector, createAttributes, content) => {
  const tag = getOrCreateMetaTag(selector, createAttributes);

  if (tag) {
    tag.setAttribute('content', content || '');
  }
};

const setCanonicalHref = (href) => {
  if (typeof document === 'undefined') {
    return;
  }

  let tag = document.querySelector('link[rel="canonical"]');

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
};

const applyBlogDetailMetadata = (article = {}, pathname = '') => {
  if (typeof document === 'undefined') {
    return;
  }

  const title = normalizeMetaText(article.title) || 'ブログ記事';
  const bodyPreview = buildBodyPreview(article.body);
  const description = truncateMetaText(article.excerpt || bodyPreview || title);
  const canonicalUrl = getAbsoluteYowanUrl(pathname || `/blog/${article.slug || ''}`);
  const imageUrl = getAbsoluteYowanUrl(
    article.primary_image?.preview_url ||
      article.primary_image?.previewUrl ||
      article.primary_image?.url ||
      article.primary_image?.thumbnail_url ||
      article.primary_image?.thumbnailUrl ||
      YOWAN_DEFAULT_OG_IMAGE,
  );

  document.title = `${title} | 揺腕法`;
  setCanonicalHref(canonicalUrl);
  setMetaContent('meta[name="description"]', { name: 'description' }, description);
  setMetaContent('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
  setMetaContent('meta[property="og:type"]', { property: 'og:type' }, 'article');
  setMetaContent('meta[property="og:title"]', { property: 'og:title' }, title);
  setMetaContent('meta[property="og:description"]', { property: 'og:description' }, description);
  setMetaContent('meta[property="og:image"]', { property: 'og:image' }, imageUrl);
  setMetaContent('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
  setMetaContent('meta[name="twitter:url"]', { name: 'twitter:url' }, canonicalUrl);
  setMetaContent('meta[name="twitter:title"]', { name: 'twitter:title' }, title);
  setMetaContent('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
  setMetaContent('meta[name="twitter:image"]', { name: 'twitter:image' }, imageUrl);
};

const BlogDetailPage = () => {
  const location = useLocation();
  const { pathname } = location;
  const metadata = getYowanRouteMeta(pathname);
  const [copyMessage, setCopyMessage] = useState('');
  const [article, setArticle] = useState(null);
  const [relatedEntries, setRelatedEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const previewSearch = getBlogDetailPreviewSearch(location.search);

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  useEffect(() => {
    const slug = pathname.split('/').filter(Boolean).pop();
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanPublicBlogDetail(slug)
      .then((payload) => {
        if (isMounted) {
          setArticle(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setArticle(null);
          setLoadError('記事を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [pathname]);

  useEffect(() => {
    const currentSlug = pathname.split('/').filter(Boolean).pop();
    let isMounted = true;

    fetchYowanPublicBlogList({ page: 1, pageSize: RELATED_BLOG_PAGE_SIZE })
      .then(async (payload) => {
        const otherEntries = payload.results.filter((entry) => entry.slug !== currentSlug);
        const nextEntries = (otherEntries.length ? otherEntries : payload.results).slice(0, 8);

        if (isMounted) {
          setRelatedEntries(nextEntries);
        }

        const entriesMissingExcerpt = nextEntries.filter((entry) => !entry.excerpt && entry.slug);

        if (!entriesMissingExcerpt.length) {
          return;
        }

        const bodyPreviewEntries = await Promise.all(
          entriesMissingExcerpt.map((entry) =>
            fetchYowanPublicBlogDetail(entry.slug)
              .then((detail) => [entry.slug, buildBodyPreview(detail.body)])
              .catch(() => [entry.slug, '']),
          ),
        );

        if (isMounted) {
          const bodyPreviewMap = new Map(bodyPreviewEntries);
          setRelatedEntries((currentEntries) =>
            currentEntries.map((entry) => ({
              ...entry,
              bodyPreview: entry.excerpt ? entry.bodyPreview : bodyPreviewMap.get(entry.slug) || entry.bodyPreview || '',
            })),
          );
        }
      })
      .catch(() => {
        if (isMounted) {
          setRelatedEntries([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [pathname]);

  useEffect(() => {
    if (article) {
      applyBlogDetailMetadata(article, pathname);
    }
  }, [article, pathname]);

  if (!article) {
    return (
      <Section>
        <PageContainer>
          <p className="yowan-admin-form__message">
            {isLoading ? <LoadingSpinner /> : loadError || '記事が見つかりませんでした。'}
          </p>
        </PageContainer>
      </Section>
    );
  }

  const currentUrl =
    typeof window !== 'undefined' ? `${window.location.origin}${pathname}${previewSearch}` : pathname;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopyMessage('URLをコピーしました。');
    } catch (error) {
      setCopyMessage('URLをコピーできませんでした。');
    }
  };

  return (
    <>
      <Section>
        <PageContainer>
          <div className="yowan-blog-detail-header">
            <div className="yowan-blog-detail-header__body">
              <div className="yowan-blog-detail-header__meta">
                <BlogAuthorAttribution author={article.author} className="yowan-blog-detail-header__author" />
                <p className="yowan-blog-detail-header__date">
                  <span className="yowan-blog-meta__label">date: </span>
                  <span>{article.date}</span>
                </p>
              </div>
              <Heading as="h1">{article.title}</Heading>
            </div>
          </div>
        </PageContainer>
      </Section>

      <Section>
        <PageContainer>
          <article className="yowan-blog-detail-article">
            {article.primary_image?.preview_url || article.primary_image?.url || article.primary_image?.thumbnail_url ? (
              <figure className="yowan-blog-detail-article__image">
                <img
                  src={article.primary_image.preview_url || article.primary_image.url || article.primary_image.thumbnail_url}
                  alt={article.primary_image.alt_text || article.title}
                />
                {article.primary_image.caption ? (
                  <figcaption>{article.primary_image.caption}</figcaption>
                ) : null}
              </figure>
            ) : article.imageLabel ? (
              <div className="yowan-blog-detail-article__image" aria-hidden="true">
                <span>{article.imageLabel}</span>
              </div>
            ) : null}
            <div className="yowan-blog-detail-article__body c-surface-content">
              {article.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </article>

          <div className="yowan-blog-detail-share">
            <div className="yowan-blog-detail-share__actions">
              <FacebookShareButton
                className="yowan-blog-detail-share__button yowan-social-icon-button"
                url={currentUrl}
                quote={article.title}
                resetButtonStyle={false}
                aria-label="Facebookで共有"
              >
                <FacebookIcon size={SOCIAL_ICON_SIZE} round />
                <span className="u-visually-hidden">Facebook</span>
              </FacebookShareButton>
              <TwitterShareButton
                className="yowan-blog-detail-share__button yowan-social-icon-button"
                url={currentUrl}
                title={article.title}
                resetButtonStyle={false}
                aria-label="Xで共有"
              >
                <TwitterIcon size={SOCIAL_ICON_SIZE} round />
                <span className="u-visually-hidden">X</span>
              </TwitterShareButton>
              <button
                type="button"
                className="yowan-blog-detail-share__button yowan-social-icon-button"
                onClick={handleCopyLink}
                aria-label="リンクをコピー"
              >
                <LinkGlyph aria-hidden="true" fontSize="small" />
                <span className="u-visually-hidden">リンク</span>
              </button>
            </div>
            {copyMessage ? <p className="yowan-blog-detail-share__message">{copyMessage}</p> : null}
          </div>

          <div className="yowan-blog-detail-related">
            <div className="yowan-blog-detail-related__track">
              {relatedEntries.map((entry) => {
                const { excerpt, truncated } = truncateRelatedExcerpt(getRelatedPreviewText(entry));
                const relatedImageSrc = entry.primary_image?.thumbnail_url || entry.primary_image?.url || '';
                const hasRelatedMedia = Boolean(relatedImageSrc || entry.imageLabel);
                const relatedExcerpt = excerpt ? (
                  <p className="yowan-blog-detail-related__excerpt">
                    {excerpt}
                    {truncated ? (
                      <span className="yowan-blog-detail-related__read-more">...read more</span>
                    ) : null}
                  </p>
                ) : null;
                const relatedHeader = (
                  <div className="yowan-blog-detail-related__body">
                    <p className="yowan-blog-detail-related__title">{entry.title}</p>
                    <BlogEntryMeta
                      author={entry.author}
                      date={entry.date}
                      className="yowan-blog-detail-related__meta"
                    />
                  </div>
                );

                return (
                  <Link
                    key={entry.href}
                    className={`yowan-blog-detail-related__card${
                      hasRelatedMedia ? '' : ' yowan-blog-detail-related__card--text'
                    }`}
                    to={appendBlogDetailPreviewQuery(entry.href, previewSearch)}
                  >
                    {relatedImageSrc ? (
                      <>
                        <div className="yowan-blog-detail-related__image">
                          <img
                            src={relatedImageSrc}
                            alt={entry.primary_image?.alt_text || entry.title}
                          />
                        </div>
                        {relatedHeader}
                        {relatedExcerpt}
                      </>
                    ) : entry.imageLabel ? (
                      <>
                        <div className="yowan-blog-detail-related__image" aria-hidden="true">
                          <span>{entry.imageLabel}</span>
                        </div>
                        {relatedHeader}
                        {relatedExcerpt}
                      </>
                    ) : (
                      <>
                        {relatedHeader}
                        {relatedExcerpt}
                      </>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        </PageContainer>
      </Section>
    </>
  );
};

export default BlogDetailPage;
